import type { RadioSelectableCardProps } from '@qbe/ads-core';
import { RadioGroup } from '@qbe/ads-core';
import React, { useEffect, useState } from 'react';

import { Option, useAppContent, useAppDispatch, useQuote } from '~/hooks';
import { updatePaymentFrequency } from '~/slices/payments';
import { updatePaymentSchedule } from '~/slices/quote';
import { qalFormStartAction } from '~/thunks/analytics';
import {
  PaymentFrequencyOptions,
  PaymentSchedule as PaymentScheduleOptions,
} from '~/types/payments';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

interface PaymentScheduleProps {
  isLoading: boolean;
}

interface LabeledOption<T = string> extends Option<T> {
  label: string;
  labelProps: {
    variant: string;
    weight: string;
  };
}

const labelProps = {
  variant: 'label2',
  weight: 'bold',
};

const paymentScheduleToFrequency = {
  [PaymentScheduleOptions.Monthly]: PaymentFrequencyOptions.Monthly,
  [PaymentScheduleOptions.Yearly]: PaymentFrequencyOptions.OneOff,
};

const PaymentSchedule: React.FC<PaymentScheduleProps> = ({ isLoading }) => {
  const dispatch = useAppDispatch();

  const { getTextContent } = useAppContent({
    basePath: 'dsme.quote',
  });

  const options: LabeledOption[] = [
    {
      id: PaymentScheduleOptions.Yearly,
      value: PaymentScheduleOptions.Yearly,
      label: getTextContent('fields.paymentschedule.oneOff'),
      labelProps,
    },
    {
      id: PaymentScheduleOptions.Monthly,
      value: PaymentScheduleOptions.Monthly,
      label: getTextContent('fields.paymentschedule.monthly'),
      labelProps,
    },
  ];

  const { paymentSchedule } = useQuote();
  const [paySchedule, setPaySchedule] = useState<PaymentScheduleOptions | null>(paymentSchedule);

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  useEffect(() => {
    setPaySchedule(paymentSchedule);
  }, [paymentSchedule]);

  const onChange = (str: string) => {
    const value = str as PaymentScheduleOptions;
    setPaySchedule(value);
    dispatch(updatePaymentSchedule(value));
    dispatch(updatePaymentFrequency(paymentScheduleToFrequency[value]));
    handleFormStart();
  };

  return (
    <RadioGroup
      title=""
      aria-label="payment schedule"
      name="row-radio-buttons-group"
      radioList={options as RadioSelectableCardProps['radioList']}
      defaultValue={paySchedule}
      onChange={onChange}
      disabled={isLoading}
      data-track={paySchedule}
      data-track-value="capture"
    />
  );
};

export default PaymentSchedule;
